import { ArrowUp, LoaderCircle, Square } from "lucide-react";
// biome-ignore lint/style/useImportType: the classic JSX transform needs React at runtime.
import React from "react";
import { useTranslation } from "react-i18next";

import type { Schedule, WatchlistScope } from "../../watchlist/types";
import { hasProcessSelection } from "../../watchlist/types";
import type { FilterOption } from "../FilterMultiSelect";
import { ChatAiDisclaimer } from "../chat/ChatAiDisclaimer";
import { QuoteTextarea } from "../chat/QuoteTextarea";
import MentionMenu from "./MentionMenu";
import WatchScopeControls from "./WatchScopeControls";
import { useMentions } from "./useMentions";

type WatchlistComposerProps = {
  /** Direct Watch setup is active, so cadence belongs in the context row. */
  settingUpWatch?: boolean;
  text: string;
  onTextChange: (text: string) => void;
  scope: WatchlistScope;
  onScopeChange: (scope: WatchlistScope) => void;
  schedule: Schedule;
  onScheduleChange: (schedule: Schedule) => void;
  userOptions: FilterOption[];
  processOptions: FilterOption[];
  onSubmit: () => void;
  busy: boolean;
  /** Abandon the answer in flight. Only offered while `busy`. */
  onStop: () => void;
  /** A review of this prompt is in flight, so submitting again would race it. */
  reviewing?: boolean;
  /** More conversational copy once the first question is already on screen. */
  placeholder?: string;
  /** Shared with transcript actions that need to focus or edit the reply. */
  inputRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  /** The landing-page composer keeps its established vertical footprint. */
  fixedHeight?: boolean;
};

/**
 * The one box every prompt is written in. Its surrounding banner establishes
 * whether the single submit action asks now or advances direct Watch setup.
 */
const WatchlistComposer = ({
  settingUpWatch = false,
  text,
  onTextChange,
  scope,
  onScopeChange,
  schedule,
  onScheduleChange,
  userOptions,
  processOptions,
  onSubmit,
  busy,
  onStop,
  reviewing = false,
  placeholder,
  inputRef,
  fixedHeight = false,
}: WatchlistComposerProps) => {
  const { t } = useTranslation();
  const mentions = useMentions({
    text,
    onTextChange,
    chosenUserIds: scope.userIds,
    chosenProcessSlugs: scope.processSlugs,
    onPick: candidate =>
      onScopeChange(
        candidate.kind === "person"
          ? { ...scope, userIds: [...scope.userIds, candidate.id] }
          : { ...scope, processSlugs: [...scope.processSlugs, candidate.id] },
      ),
    userOptions,
    processOptions,
  });

  const submitDisabled =
    text.trim().length === 0 ||
    scope.userIds.length === 0 ||
    !hasProcessSelection(scope) ||
    busy ||
    reviewing;

  // Tint mentions of whatever is currently in scope, so a completed `@name`
  // reads as recognized rather than as plain prose.
  const mentionTokens = [
    ...userOptions.filter(option => scope.userIds.includes(option.id)),
    ...processOptions.filter(option => scope.processSlugs.includes(option.id)),
  ].map(option => `@${option.label}`);

  return (
    <div>
      <fieldset
        disabled={reviewing}
        className="min-w-0 rounded-2xl border border-outline bg-surface p-3 shadow-sm transition focus-within:border-primary/40"
      >
        {/* The prompt gets its own line. Context has one representation in the
          controls below; duplicating selected chips above it made defaults
          look like fields the author had deliberately filled in. */}
        <div className="relative">
          <QuoteTextarea
            ref={node => {
              mentions.textareaRef.current = node;
              if (inputRef) inputRef.current = node;
            }}
            value={text}
            onChange={event => {
              onTextChange(event.target.value);
              mentions.sync(event.target.value, event.target.selectionStart);
            }}
            onClick={event => mentions.sync(text, event.currentTarget.selectionStart)}
            onBlur={mentions.close}
            onKeyDown={event => {
              if (mentions.handleKeyDown(event)) return;
              // Enter sends; Shift+Enter remains available for multi-line prompts.
              // Do not submit while an IME composition is still being confirmed.
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                if (!submitDisabled) onSubmit();
              }
            }}
            rows={3}
            maxHeight={160}
            fixedHeight={fixedHeight}
            highlightTokens={mentionTokens}
            aria-autocomplete="list"
            aria-controls={mentions.menu?.id}
            aria-activedescendant={
              mentions.menu?.candidates.length
                ? `${mentions.menu.id}-${mentions.menu.activeIndex}`
                : undefined
            }
            placeholder={placeholder ?? t("watchlist.ask_placeholder")}
            className="w-full resize-none bg-transparent px-3 py-2 text-sm text-text outline-none placeholder:text-text-dim"
          />
          {mentions.menu && <MentionMenu {...mentions.menu} />}
        </div>

        {/* Two groups, not one wrapping row: the scope pills wrap among
          themselves while the actions keep their place at the right. Sharing
          one row meant a pill appearing — or Send merely growing into
          "Checking…" — could push the actions onto a line of their own. The
          groups are top-aligned, not centred: when the pills do wrap, centring
          floated the actions between the two pill rows instead of leaving them
          level with the first. */}
        <div className={`mt-2 flex gap-2 ${settingUpWatch ? "flex-col" : "items-start"}`}>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            <WatchScopeControls
              scope={scope}
              onScopeChange={onScopeChange}
              schedule={settingUpWatch ? schedule : undefined}
              onScheduleChange={settingUpWatch ? onScheduleChange : undefined}
              userOptions={userOptions}
              processOptions={processOptions}
            />
          </div>

          <div className={`flex shrink-0 items-center gap-2 ${settingUpWatch ? "self-end" : ""}`}>
            {busy ? (
              <button
                type="button"
                onClick={onStop}
                aria-label={t("chat_stop")}
                title={t("chat_stop")}
                className="flex min-w-[7rem] items-center justify-center gap-2 rounded-full border border-outline px-3 py-1.5 text-xs font-medium text-text-mid transition hover:bg-muted-surface hover:text-text"
              >
                {t("chat_stop")}
                <Square size={14} />
              </button>
            ) : (
              <button
                type="button"
                data-tour="watchlist-send"
                aria-busy={reviewing}
                onClick={onSubmit}
                disabled={submitDisabled}
                // A floor wide enough for the longer "Checking…" label, so
                // switching between the two does not resize the button and
                // squeeze the pills beside it into wrapping.
                className="flex min-w-[7rem] items-center justify-center gap-2 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-40"
              >
                {t(
                  reviewing
                    ? "watchlist.reviewing"
                    : settingUpWatch
                      ? "watchlist.setup_continue"
                      : "send",
                )}
                {reviewing ? (
                  <LoaderCircle size={14} className="animate-spin" aria-hidden="true" />
                ) : (
                  <ArrowUp size={14} />
                )}
              </button>
            )}
          </div>
        </div>
      </fieldset>
      <ChatAiDisclaimer />
    </div>
  );
};

export default WatchlistComposer;
